import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import Icon from 'components/icon/Icon';
import { cn, isNumber } from 'lib/utils';
import { buttonVariants } from 'components/ui/button';
import { PATH_ROUTES } from 'routes/path';
import { HealthReadingData } from 'services/face-scan/type';
import { ReadingDataWithSubParameters } from '../../types';
import { useGetHealthReadingById } from 'services/face-scan/hooks';
import { formatScanData } from 'lib/health-readings';
import { Skeleton } from 'components/ui/skeleton';

interface DraggableVitalsProps {
  scanResults: HealthReadingData[] | undefined;
  selectedReadingId: string;
}

export default function DraggableVitals({
  scanResults,
  selectedReadingId,
}: DraggableVitalsProps) {
  const { t } = useTranslation();

  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hoveredVitalIndex, setHoveredVitalIndex] = useState<number>();

  const { data: readingData, isFetching } = useGetHealthReadingById({
    readingId: selectedReadingId,
    enabled: !!scanResults?.length && !!selectedReadingId,
  });

  const onMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    if (containerRef?.current?.offsetLeft)
      setStartX(e.pageX - containerRef.current.offsetLeft);
    if (containerRef?.current?.scrollLeft)
      setScrollLeft(containerRef.current.scrollLeft);
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    if (containerRef?.current?.offsetLeft) {
      const x = e.pageX - containerRef.current.offsetLeft;
      const walk = (x - startX) * 3;
      containerRef.current.scrollLeft = scrollLeft - walk;
    }
  };

  const onMouseUp = () => setIsDragging(false);

  const scanData = useMemo(
    () => formatScanData(readingData, 0),
    [readingData]
  ) as ReadingDataWithSubParameters[];

  if (isFetching) {
    return (
      <div
        className='flex col-span-7 overflow-x-scroll gap-x-2 hide-scrollbar scroll-smooth cursor-grab'
        role='progressbar'
      >
        {[...Array(8)].map((_, i) => (
          <Skeleton
            className='w-28 bg-primary/10 h-44 shrink-0 rounded-2xl'
            key={`skeleton-${i}`}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className='flex w-full col-span-7 overflow-hidden overflow-x-scroll h-44 lg:h-full gap-x-2 hide-scrollbar scroll-smooth cursor-grab'
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      ref={containerRef}
    >
      {scanData?.slice(0, 10).map((scan, i) => {
        if (['Blood Pressure', 'Stress level'].includes(scan.name)) return null;

        return (
          <motion.div
            key={i}
            className='p-3 text-white h-44 lg:h-full lg:w-24 bg-primary rounded-2xl shrink-0 group selection:bg-transparent'
            onMouseEnter={() => setHoveredVitalIndex(i)}
            onMouseLeave={() => setHoveredVitalIndex(undefined)}
            animate={{
              width: hoveredVitalIndex === i ? '350px' : '120px',
            }}
            transition={{ duration: 0.3 }}
          >
            <div
              className={cn(
                'flex-col justify-between h-full',
                hoveredVitalIndex === i ? 'hidden' : 'flex'
              )}
            >
              <div className='flex flex-col items-center max-lg:mb-2'>
                <div className='flex items-center h-10'>
                  <h2 className='text-sm font-semibold leading-none text-center'>
                    {scan.name}
                  </h2>
                </div>
                <h1 className='text-xl font-semibold leading-none'>
                  {scan.value === 'Unknown' ? 'N/A' : scan.value}
                </h1>
                <p className='text-base font-medium leading-none'>{scan.unit}</p>
              </div>

              <div className='flex flex-col items-center gap-4'>
                <Icon icon={scan.key} size={36} />
                <div
                  className='w-4/5 h-1 mx-auto rounded-2xl'
                  style={{ backgroundColor: scan.color }}
                />
              </div>
            </div>

            <motion.div
              className='flex-col max-h-[20vh]'
              animate={{
                display: hoveredVitalIndex === i ? 'flex' : 'none',
                opacity: hoveredVitalIndex === i ? 1 : 0,
              }}
            >
              <h2 className='text-lg font-medium'>{scan.name}</h2>

              <div
                className={cn(
                  'grid items-start mt-2 gap-x-2',
                  isNumber(scan.value) ? 'grid-cols-6' : 'grid-cols-7'
                )}
              >
                <div
                  className={cn(
                    'flex flex-col items-center space-y-4',
                    isNumber(scan.value) ? 'col-span-1' : 'col-span-2'
                  )}
                >
                  <div className='flex flex-col items-center'>
                    <h2 className='text-2xl font-semibold leading-none min-w-fit '>
                      {scan.value === 'Unknown' ? 'N/A' : scan.value}
                    </h2>
                    <p className='text-lg font-medium leading-none'>
                      {scan.unit}
                    </p>
                  </div>

                  <Icon icon={scan.key} size={40} />
                </div>

                <div className='col-span-5 space-y-2'>
                  <p className='text-sm leading-tight text-right'>
                    {scan.short_intro}
                  </p>
                  <div className='flex justify-end'>
                    <Link
                      to={`${PATH_ROUTES.FACE_SCAN}?scan_id=${readingData?.readings[0].reading_id}&vital=${scan.key}`}
                      className={buttonVariants({
                        size: 'sm',
                        className:
                          'h-8 text-sm font-normal !bg-black/30 rounded-3xl !hover:bg-black/20',
                      })}
                    >
                      {t('see_details')}
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        );
      })}
    </div>
  );
}
